import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Package, LogOut, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  fulfilled: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  shipped: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  unfulfilled: "bg-[#E8A851]/10 text-[#E8A851] border-[#E8A851]/40",
  pending: "bg-[#E8A851]/10 text-[#E8A851] border-[#E8A851]/40",
  refunded: "bg-red-500/15 text-red-300 border-red-500/40",
};

function StatusPill({ status }) {
  const key = (status || "").toLowerCase();
  const cls = STATUS_STYLE[key] || "bg-white/5 text-zinc-300 border-white/20";
  return (
    <span
      data-testid={`order-status-${key}`}
      className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] border ${cls}`}
    >
      {status || "—"}
    </span>
  );
}

function OrderRow({ order, productMap }) {
  const [open, setOpen] = useState(false);
  const items = order.items || [];
  const displayStatus = order.fulfillment_status || order.status;
  const summary = items
    .map((it) => `${it.quantity}× ${productMap[it.sku]?.name || it.sku}`)
    .join(", ");
  const addr = order.shipping_address;

  return (
    <div data-testid={`account-order-${order.id}`} className="p-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-6 text-left"
        data-testid={`account-order-toggle-${order.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <p className="text-xs uppercase tracking-[0.15em] text-zinc-500">
              {new Date(order.created_at).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </p>
            <StatusPill status={displayStatus} />
          </div>
          <p className="font-display text-lg truncate">{summary || "—"}</p>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <p className="font-display text-2xl font-semibold">
            ${Number(order.amount_total || 0).toFixed(2)}
          </p>
          <ChevronDown
            className={`w-4 h-4 text-zinc-400 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {open && (
        <div
          data-testid={`account-order-details-${order.id}`}
          className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 pt-6 border-t border-white/10"
        >
          {/* Items */}
          <div className="lg:col-span-2">
            <p className="eyebrow mb-3">Items</p>
            <div className="border border-white/10 divide-y divide-white/10">
              {items.map((it, i) => {
                const p = productMap[it.sku];
                return (
                  <div
                    key={`${it.sku}-${i}`}
                    className="p-4 flex items-center justify-between gap-4"
                    data-testid={`order-item-${it.sku}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">
                        {p?.name || it.sku}
                      </p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        SKU {it.sku} · qty {it.quantity}
                      </p>
                    </div>
                    {p && (
                      <p className="text-sm text-zinc-300 shrink-0">
                        ${(Number(p.unit_price) * it.quantity).toFixed(2)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <dl className="mt-4 text-sm space-y-1.5">
              <div className="flex justify-between text-zinc-400">
                <dt>Subtotal</dt>
                <dd className="text-zinc-200">
                  ${Number(order.subtotal || 0).toFixed(2)}
                </dd>
              </div>
              <div className="flex justify-between text-zinc-400">
                <dt>Shipping</dt>
                <dd className="text-zinc-200">
                  ${Number(order.shipping || 0).toFixed(2)}
                </dd>
              </div>
              <div className="flex justify-between text-zinc-400">
                <dt>Tax</dt>
                <dd className="text-zinc-200">
                  ${Number(order.tax || 0).toFixed(2)}
                </dd>
              </div>
              <div className="flex justify-between pt-2 border-t border-white/10 mt-2">
                <dt className="text-white font-medium">Total</dt>
                <dd className="text-white font-semibold">
                  ${Number(order.amount_total || 0).toFixed(2)}{" "}
                  <span className="text-xs text-zinc-500 uppercase">
                    {order.currency || "usd"}
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          {/* Shipping */}
          <div>
            <p className="eyebrow mb-3">Shipping to</p>
            {addr ? (
              <div className="border border-white/10 p-4 text-sm text-zinc-300 leading-relaxed">
                {order.customer_name && (
                  <p className="text-white">{order.customer_name}</p>
                )}
                <p>{addr.line1}</p>
                {addr.line2 && <p>{addr.line2}</p>}
                <p>
                  {addr.city}
                  {addr.state ? `, ${addr.state}` : ""} {addr.postal_code}
                </p>
                <p>{addr.country}</p>
              </div>
            ) : (
              <p className="text-xs text-zinc-500">
                No shipping address on file.
              </p>
            )}
            <p className="eyebrow mt-6 mb-2">Order ID</p>
            <p className="text-xs font-mono text-zinc-500 break-all">
              {order.id}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Account() {
  const { user, logout, authHeader } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios
        .get(`${API}/orders/mine`, { headers: authHeader() })
        .then((r) => setOrders(r.data))
        .catch(() => setOrders([])),
      axios
        .get(`${API}/products`)
        .then((r) => setProducts(r.data))
        .catch(() => setProducts([])),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const productMap = useMemo(() => {
    const m = {};
    products.forEach((p) => {
      m[p.id] = p;
    });
    return m;
  }, [products]);

  return (
    <div data-testid="account-page" className="bg-[#050505] text-white pt-32 min-h-screen">
      <div className="px-6 md:px-12 lg:px-24 pb-24 max-w-4xl mx-auto">
        <p className="eyebrow mb-4">Your Account</p>
        <div className="flex items-start justify-between gap-6 mb-12">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">
              Welcome, {user?.name?.split(" ")[0]}.
            </h1>
            <p className="text-zinc-400 mt-3 text-sm">{user?.email}</p>
          </div>
          <button
            data-testid="account-logout-button"
            onClick={logout}
            className="btn-secondary !text-zinc-300 !border-white/20"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        {/* Affiliate CTA */}
        <div className="border border-[#E8A851]/30 bg-gradient-to-br from-[#E8A851]/[0.07] to-transparent p-6 mb-10 flex items-center justify-between gap-6 flex-wrap">
          <div>
            <p className="eyebrow mb-1">Become an affiliate</p>
            <p className="text-sm text-zinc-300">
              Earn 5–10% on every Aura Sleeve sold through your ref link.
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/affiliate"
              data-testid="account-affiliate-apply-link"
              className="btn-secondary !border-[#E8A851]/40 !text-[#E8A851]"
            >
              Apply
            </Link>
            <Link
              to="/affiliate/dashboard"
              data-testid="account-affiliate-dashboard-link"
              className="btn-secondary !border-white/20"
            >
              My dashboard
            </Link>
          </div>
        </div>

        <div className="border border-white/10 bg-white/[0.02]">
          <div className="p-6 border-b border-white/10 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-[#E8A851]" />
              <p className="font-display text-xl font-medium">Order history</p>
            </div>
            {!loading && orders.length > 0 && (
              <p className="text-xs uppercase tracking-[0.15em] text-zinc-500">
                {orders.length} order{orders.length === 1 ? "" : "s"}
              </p>
            )}
          </div>

          {loading ? (
            <div className="p-8 text-zinc-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : orders.length === 0 ? (
            <div data-testid="account-no-orders" className="p-12 text-center">
              <p className="text-zinc-400 mb-6">
                You haven't placed any orders yet.
              </p>
              <Link
                to="/product"
                data-testid="account-shop-link"
                className="btn-primary inline-flex"
              >
                Shop Aura Sleeve
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {orders.map((o) => (
                <OrderRow key={o.id} order={o} productMap={productMap} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
